import { useMemo } from 'react';

import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { faCalculatorSimple } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, TableBody, TableHead, TableRow, Typography } from '@mui/material';

import { calculationQueries } from '@repo/api/claim/calculations';
import { isCalculatedCollectible } from '@repo/api/claim/calculations.types';
import { claimQueries } from '@repo/api/claim/claims';
import { questionnaireQueries } from '@repo/api/claim/questionnaire';
import { countryQueries } from '@repo/api/countries';
import { getFootnoteLetter } from '@repo/features/calculations';
import useCurrency from '@repo/features/hooks/useCurrency';

import { CalculationTable } from './-components/CalculationTable';
import { Cell } from './-components/Cell';
import { CurrencyCell } from './-components/CurrencyCell';
import { Footnote } from './-components/Footnote';

const isBenefitsAndTaxes = (description: string) =>
	description === 'Benefits & Taxes';

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/calculations/average',
)({
	component: function AverageCalculationsPage() {
		const { claimId } = Route.useParams();

		const [
			{ data: claim },
			{ data: average },
			{ data: countries },
			{ data: questionnaire },
		] = useSuspenseQueries({
			queries: [
				claimQueries.id(claimId),
				calculationQueries.collectible(claimId),
				countryQueries.list(),
				questionnaireQueries.id(claimId),
			],
		});

		const currency = useCurrency(claim.claimInsured.countryId);
		const isTierTwo = claim.isTier2Enabled && questionnaire.isTier2;
		const isTierTwoMonthly =
			isTierTwo && claim.estimatedPeriodofIndemnityDateType === 2;

		const countryName = useMemo(
			() => countries.find((c) => c.id === claim.claimInsured.countryId)?.name,
			[countries, claim.claimInsured.countryId],
		);

		const footnotes = useMemo(() => {
			if (!isCalculatedCollectible(average)) {
				return [];
			}

			const benefitsFootnote =
				countryName === 'Canada' ?
					'Based on relationship of benefits and taxes to labour. All benefits and taxes are grouped as one expense and gets applied to uninsured payroll.'
				:	'Calculated based on response to Business Questionnaire. All benefits and taxes are grouped as one expense and gets applied to uninsured payroll.';

			return (
					average.variableCosts.some((variableCost) =>
						isBenefitsAndTaxes(variableCost.description),
					)
				) ?
					[
						{
							note: benefitsFootnote,
							expenseCategory: 'Benefits & Taxes',
							expenseSection: 'Variable Costs',
						},
					]
				:	[];
		}, [average, countryName]);

		if (!isCalculatedCollectible(average)) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						p: 4,
						gap: 1,
						border: '1px solid',
						borderColor: 'accent.main',
						width: '600px',
						maxWidth: '100%',
						mx: 'auto',
					}}
				>
					<FontAwesomeIcon icon={faCalculatorSimple} size="2xl" />
					<Typography variant="subtitle1">No Calculations Data</Typography>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					width: '600px',
					maxWidth: '100%',
					overflow: 'auto hidden',
				}}
			>
				<CalculationTable>
					<TableHead>
						<TableRow>
							<Cell header>Description</Cell>
							<Cell header nowrap sx={{ width: 0 }}>
								Percent
							</Cell>
							<Cell header>Amount</Cell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<Cell />
							<Cell />
							<Cell header small>
								({currency.display ?? currency.currency})
							</Cell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Annual Sales</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency} underline="double">
								{average.annualSales}
							</CurrencyCell>
						</TableRow>
						{isTierTwo && (
							<TableRow className="striped">
								<Cell>Sales Trend</Cell>
								<Cell percent align="center">
									{average.salesTrend}
								</Cell>
								<Cell />
							</TableRow>
						)}
						<TableRow className="striped">
							<Cell>Projected Sales</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency} underline>
								{average.projectedSales}
							</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						{average.variableCosts.length > 0 && (
							<>
								<TableRow>
									<Cell header colSpan={3} align="left">
										Less: Variable Costs
									</Cell>
								</TableRow>
								{average.variableCosts.map((variableCost, i, a) => {
									const hasBenefitsAndTaxesFootnote = isBenefitsAndTaxes(
										variableCost.description,
									);
									return (
										<TableRow
											key={variableCost.description}
											className="striped"
										>
											<Cell indent>{variableCost.description}</Cell>
											{!hasBenefitsAndTaxesFootnote ?
												<Cell percent align="center">
													{variableCost.percent}
												</Cell>
											:	<Cell align="right">
													<Footnote>A</Footnote>
												</Cell>
											}
											<CurrencyCell
												{...currency}
												hideSymbol
												underline={i === a.length - 1}
												noZero
											>
												{variableCost.amount}
											</CurrencyCell>
										</TableRow>
									);
								})}
								<TableRow>
									<Cell spacer />
								</TableRow>
								<TableRow className="striped">
									<Cell>Total Variable Costs</Cell>
									<Cell />
									<CurrencyCell {...currency} hideSymbol>
										{average.totalVariableCosts}
									</CurrencyCell>
								</TableRow>
								<TableRow>
									<Cell spacer />
								</TableRow>
							</>
						)}
						<TableRow className="striped">
							<Cell>Projected Annual BI Value</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency} underline="double">
								{average.projectedAnnualBiValue}
							</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						{average.maxIndemnityPeriodSales ?
							<>
								<TableRow className="striped">
									<Cell>Maximum Indemnity Period BI Value</Cell>
									<Cell />
									<CurrencyCell {...currency} hideSymbol>
										{average.maxIndemnityPeriodSales}
									</CurrencyCell>
								</TableRow>
								<TableRow>
									<Cell spacer />
								</TableRow>
							</>
						:	null}
						<TableRow className="striped">
							<Cell>Sum Insured</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency}>{average.sumInsured}</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Average Adjustment</Cell>
							<Cell percent align="right" underline="double">
								{average.averageAdjustment}
							</Cell>
							<Cell />
						</TableRow>
					</TableBody>
				</CalculationTable>
				<Box
					sx={{
						mt: 2,
					}}
				>
					<Typography
						variant="body2"
						sx={{
							padding: '6px 8px 6px 12px',
						}}
					>
						Notes
					</Typography>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'auto auto 1fr',
							columnGap: 1,
						}}
					>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'subgrid',
								gridColumn: '1 / -1',
								color: 'grey.700',
								p: '1px',
								'& .footnote': {
									padding: '6px 8px 6px 28px',
									display: 'grid',
									gridTemplateColumns: 'subgrid',
									gridColumn: '1 / -1',
									width: '100%',
									'& p': {
										display: 'flex',
										gap: 1,
									},
								},
							}}
							id={'footnote-extra'}
						>
							<Box className="footnote">
								<Typography variant="body2"></Typography>
								<Typography variant="body2">-</Typography>
								<Typography variant="body2">
									{isTierTwoMonthly ?
										'For coinsurance purposes, we have assumed sales trending for the sales loss calculation would be representative of the coinsurance requirements and, therefore, we have applied trending.'
									:	'For coinsurance purposes, we have assumed the uploaded financial statement is representative of the coinsurance requirements and, therefore, have not applied any trending.'
									}
								</Typography>
							</Box>
						</Box>
						{footnotes.map((f, i) => (
							<Box
								key={f.expenseCategory + f.expenseSection}
								sx={{
									display: 'grid',
									gridTemplateColumns: 'subgrid',
									gridColumn: '1 / -1',
									p: '1px',
									'& a.footnote': {
										padding: '6px 8px 6px 28px',
										display: 'grid',
										gridTemplateColumns: 'subgrid',
										gridColumn: '1 / -1',
										width: '100%',
										textDecoration: 'none',
										'&.active': {
											outline: '1px solid',
											outlineColor: 'accent.main',
											borderRadius: 1,
											backgroundColor: 'offwhite.light',
										},
										'& p': {
											display: 'flex',
											gap: 1,
										},
									},
								}}
								id={'footnote-' + getFootnoteLetter(i)}
							>
								<Footnote
									footnoteLetter={getFootnoteLetter(i)}
									className="footnote"
								>
									<Typography variant="body2">
										{getFootnoteLetter(i)}
									</Typography>
									<Typography variant="body2">-</Typography>
									<Typography variant="body2">{f.note}</Typography>
								</Footnote>
							</Box>
						))}
					</Box>
				</Box>
			</Box>
		);
	},
});
